import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  deletePppcPolicy,
  findPppcPolicyForApp,
  listPppcPolicies,
  savePppcPolicy,
} from "@/services/pppcApi";
import { SavePppcArgs } from "@/types/pppc";
import { toast } from "sonner";
import { recordPppcActivity } from "@/services/pppcActivityLog";
import { getCurrentUserEmail } from "@/lib/auth";
import { reconcilePolicyTags } from "@/services/tagAssignmentApi";
import * as tagsApi from "@/services/tagsApi";
import { mapApiTagMinimal, MappedTagMinimal } from "@/utils/tagMapping";

export const pppcQueryKey = (bundleId?: string) => ["pppc-policy", bundleId ?? ""];
export const pppcLibraryQueryKey = ["pppc-policies"] as const;
export const pppcPolicyTagsQueryKey = (policyId?: string) => [
  "pppc-policy-tags",
  policyId ?? "",
];

/** Tags currently assigned to a single PPPC policy. */
export function usePppcPolicyTags(policyId?: string) {
  return useQuery({
    queryKey: pppcPolicyTagsQueryKey(policyId),
    queryFn: async (): Promise<MappedTagMinimal[]> => {
      const tags = await tagsApi.getTagsByPolicy(policyId!);
      return (tags || []).map((t: any) => mapApiTagMinimal(t));
    },
    enabled: !!policyId,
  });
}

export function usePppcPolicy(bundleId?: string) {
  return useQuery({
    queryKey: pppcQueryKey(bundleId),
    queryFn: () => findPppcPolicyForApp(bundleId!),
    enabled: !!bundleId,
  });
}

export function useAllPppcPolicies() {
  return useQuery({
    queryKey: pppcLibraryQueryKey,
    queryFn: () => listPppcPolicies(),
  });
}

export function useSavePppcPolicy(bundleId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: SavePppcArgs) => {
      const result = await savePppcPolicy(args);
      const actor = getCurrentUserEmail() || "system";

      // Reconcile deployment tags using the same contract as App Config so
      // both wizards stay behaviourally aligned. The PPPC `created/updated`
      // and `tagged` audit-log entries are written by the service layer
      // (pppcApi.savePppcPolicy + tagAssignmentApi.reconcilePolicyTags), so
      // the hook only needs to pass enough context for the audit entry.
      const previousTagIds = args.previousTagIds ?? [];
      const nextTagIds = args.tagIds ?? [];
      const prevSet = new Set(previousTagIds);
      const nextSet = new Set(nextTagIds);
      const added = nextTagIds.filter((id) => !prevSet.has(id));
      const removed = previousTagIds.filter((id) => !nextSet.has(id));
      const ranReconcile = added.length > 0 || removed.length > 0;

      let reconcileError: unknown;
      try {
        await reconcilePolicyTags({
          policyId: result.id,
          previousTagIds,
          nextTagIds,
          actor,
          source: "useSavePppcPolicy",
          audit: {
            kind: "pppc",
            bundleId: args.bundleId,
            appName: args.appName,
            reusedExistingPolicy: !!args.existingPolicyId,
          },
        });
      } catch (err) {
        // Capture so the policy save isn't reported as a hard failure when
        // only the tag-reconcile leg failed; the toast surfaces the partial
        // outcome and the audit log retains the full call detail.
        reconcileError = err;
      }

      return {
        ...result,
        reconcile: {
          ran: ranReconcile,
          added,
          removed,
          previousCount: previousTagIds.length,
          nextCount: nextTagIds.length,
          // "skipped" reasons the UI can render verbatim:
          //   - "no-tag-changes": before/after sets matched
          //   - "no-tags-bound": no tags were assigned at all (catalog deploy)
          skippedReason: ranReconcile
            ? null
            : nextTagIds.length === 0
              ? ("no-tags-bound" as const)
              : ("no-tag-changes" as const),
          error: reconcileError,
        },
      };
    },
    onSuccess: (data, variables) => {
      qc.invalidateQueries({ queryKey: pppcQueryKey(bundleId) });
      qc.invalidateQueries({ queryKey: pppcLibraryQueryKey });
      qc.invalidateQueries({ queryKey: ["policies"] });
      if (variables.existingPolicyId) {
        qc.invalidateQueries({
          queryKey: pppcPolicyTagsQueryKey(variables.existingPolicyId),
        });
      }

      // Build a single, clear message explaining whether tag reconciliation
      // ran or was skipped, and why. One sonner toast per save (project
      // standard: max 1 active feedback toast).
      const r = data.reconcile;
      if (r.error) {
        const msg = r.error instanceof Error ? r.error.message : "tag update failed";
        toast.error(`PPPC policy saved, but tag assignment failed: ${msg}`);
        return;
      }
      if (!r.ran) {
        const reason =
          r.skippedReason === "no-tags-bound"
            ? "no tags assigned — deployment follows the app"
            : "tag selection unchanged";
        toast.success("PPPC policy saved", {
          description: `Tag reconciliation skipped: ${reason}.`,
        });
        return;
      }
      const parts: string[] = [];
      if (r.added.length) parts.push(`+${r.added.length} added`);
      if (r.removed.length) parts.push(`-${r.removed.length} removed`);
      toast.success("PPPC policy saved", {
        description: `Tag reconciliation ran: ${parts.join(", ")} (now ${r.nextCount} tag${r.nextCount === 1 ? "" : "s"}).`,
      });
    },
    onError: (err: unknown) => {
      // PppcValidationError carries a friendly summary in .message, so this
      // path covers both server-side validation failures and network errors.
      const msg = err instanceof Error ? err.message : "Failed to save PPPC policy";
      toast.error(msg);
    },
  });
}

export function useDeletePppcPolicy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await deletePppcPolicy(id);
      recordPppcActivity({
        policyId: id,
        action: "deleted",
        actor: getCurrentUserEmail(),
        summary: "PPPC policy deleted",
      });
      return id;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: pppcLibraryQueryKey });
      qc.invalidateQueries({ queryKey: ["policies"] });
      toast.success("PPPC policy deleted");
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : "Failed to delete PPPC policy";
      toast.error(msg);
    },
  });
}
